import { useQuery } from 'react-query';
import { api } from '../constants';
import { AxiosInstance } from '../axios.config';
import { IOrderTracking } from '../returnOrders/types';
import { Order } from '../OrderManagement/types';

export type ISalesSummaryResponse = {
  vendor_id: number;
  filter_type: string;
  metrics: {
    sales: {
      current: number;
      previous: number;
      change_percentage: number;
      trend: string;
    };
    quantity: {
      current: number;
      previous: number;
      change_percentage: number;
      trend: string;
    };
    customers: {
      current: number;
      previous: number;
      change_percentage: number;
      trend: string;
    };
    orders: {
      current: number;
      previous: number;
      change_percentage: number;
      trend: string;
    };
  };
};

const getSalesSummary = async (filterType: string): Promise<ISalesSummaryResponse> => {
  const response = await AxiosInstance.get(
    api.dashboard.salesSummary + '?filter_type=' + filterType,
  );
  return response.data;
};

const useGetSalesSummary = (filterType: string) => {
  return useQuery(['salesSummary', filterType], () => getSalesSummary(filterType));
};

export type ITotalOrdersResponse = {
  filter_type: string;
  metric: string;
  data: {
    month: string;
    categories: {
      category: string;
      total_value: number;
    }[];
  }[];
};

const getTotalOrder = async (
  filterType: string,
  order: boolean,
  quantity: boolean,
): Promise<ITotalOrdersResponse> => {
  let url = `${api.dashboard.totalOrder}?filter_type=${filterType}`;
  if (quantity) {
    url += `&quantity=${quantity}`;
  }
  if (order) {
    url += `&orders=${order}`;
  }
  const response = await AxiosInstance.get(url);
  return response.data;
};

const useGetTotalOrder = (filterType: string, quantity: boolean, order: boolean) => {
  return useQuery(['totalOrder', filterType, quantity, order], () =>
    getTotalOrder(filterType, quantity, order),
  );
};
export interface ITotalSellResponse {
  filter_type: string;
  date_range: {
    start_date: string;
    end_date: string;
  };
  total_sales: number;
  total_quantity: number;
  currency: string;
  category_breakdown: {
    category: string;
    total_quantity: number;
    quantity_percentage: number;
    total_sales: number;
    sales_percentage: number;
  }[];
}

const getTotalSell = async (filterType: string): Promise<ITotalSellResponse> => {
  const response = await AxiosInstance.get(api.dashboard.totalSell + '?filter_type=' + filterType);
  return response.data;
};

const useGetTotalSell = (filterType: string) => {
  return useQuery(['totalSell', filterType], () => getTotalSell(filterType));
};

export interface ITopSellingProduct {
  product_name: string;
  weekly_sales: number;
  total_sales: number;
  percentage_contribution: number;
}

export interface ITopSellResponse {
  top_selling_products: ITopSellingProduct[];
}

const getTopSell = async (): Promise<ITopSellResponse> => {
  const response = await AxiosInstance.get(api.dashboard.topSell);
  return response.data;
};

const useGetTopSell = () => {
  return useQuery('topSell', getTopSell);
};

const getRecentOrder = async (): Promise<IOrderTracking[]> => {
  const response = await AxiosInstance.get(api.dashboard.recentOrder + '?page=1');
  return response.data.results;
};

const useGetRecentOrder = () => {
  return useQuery('recentOrder', getRecentOrder);
};

const getPendingOrder = async (): Promise<Order[]> => {
  const response = await AxiosInstance.get(`${api.dashboard.pendingOrder}?page=1&page_size=5`);
  return response.data.results;
};

const useGetPendingOrder = () => {
  return useQuery('pendingOrder', getPendingOrder);
};

export {
  useGetSalesSummary,
  useGetTotalOrder,
  useGetTotalSell,
  useGetTopSell,
  useGetRecentOrder,
  useGetPendingOrder,
};
